import { ImageType, isAvailable } from "../../../types/baseTypes";
import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { MediaItem } from "../../Global/MediaItem/MediaItem";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { Header_Pages } from "@/wp/types/acf-header-types";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import Split from "@/components/_primitives/Split/Split";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@/wp/types/client";
import { ParentsType } from "@/wp/getParentPages";
import { Content } from "@/components/Global/Content/Content";
import { useRouter } from "next/router";

export interface GlossarHeaderProps {
  post: Post<"glossary">;
  headerOptions?: HeadersOptionsType["header_glossary"];
  parents: ParentsType;
  fallbackImg?: ImageType | false;
}

export const isAnyImageAvailable = (
  post: Header_Pages["header_seite"],
): boolean => {
  return (
    isAvailable<ImageType>(post.media.image_fullhd) ||
    isAvailable<ImageType>(post.media.image_desktop) ||
    isAvailable<ImageType>(post.media.image_tablet) ||
    isAvailable<ImageType>(post.media.image_mobile)
  );
};

export const GlossarHeader = ({
  post,
  headerOptions,
  parents,
  fallbackImg,
}: GlossarHeaderProps) => {
  const header = post.acf.header_glossary;
  const router = useRouter();

  const {
    overline = "",
    subline = "",
    media,
    type,
    background_color_class,
    section_margin_class,
    link,
    link_extended,
    content,
    headline_text_align_class,
    row_direction_class,
    // has_cta,
    // cta,
    // link_automatically,
    breadcrumb_bg_color,
  } = header;

  const { textheader, imageheader, splitheader, show_extended_link } =
    headerOptions ?? {};

  const { text_width: textheaderTextWidth } = textheader ?? {};

  const {
    text_horizontal_align: imageheaderTextHorizontalAlign,
    text_vertical_align: imageheaderTextVerticalAlign,
    vertical_align: imageheaderVerticalAlign,
    headline_gradient: imageheaderHeadlineGradient,
    text_width: imageheaderTextWidth,
    image_height: imageheaderImageHeight,
  } = imageheader ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
    header_width: splitheaderWidth,
    text_vertical_align: splitheaderTextVerticalAlign,
    text_width_with_spacer: splitheaderTextWidthWithSpacer,
  } = splitheader ?? {};

  const splitHeaderDirection: "row" | "row-reverse" =
    row_direction_class === "is-row-normal-desktop" ? "row-reverse" : "row";

  /* jscpd:ignore-start */
  const { showOverline, showSubline } = useSublinesVisibility("none");
  /* jscpd:ignore-end */

  const mediaJSX = (
    <MediaItem
      image_desktop={
        media.image_desktop
          ? media.image_desktop
          : fallbackImg
            ? fallbackImg
            : false
      }
      image_tablet={media.image_tablet}
      image_mobile={media.image_mobile}
      image_fullhd={media.image_fullhd}
      // mediaFormat={imageheaderMediaFormat}
      sizes="(max-width: 767px) 50vw, 100vw"
      priority={true}
      mediaFormat={
        header.type === "splitheader" ? splitheaderMediaFormat : undefined
      }
      className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
    />
  );

  const contentJSX = (
    <>
      <Content
        headline={header.title}
        headline_html_tag="h1-pages"
        headline_text_align_class={headline_text_align_class}
        headlineExtraClass={"is-flex is-flex-direction-column"}
        text_align_class={headline_text_align_class}
        overline={showOverline ? overline : ""}
        subline={
          router.query.keyword
            ? (router.query.keyword as string)
            : showSubline
              ? subline
              : ""
        }
        button_type="primary"
        text={content}
        link={!show_extended_link && link}
        link_extended={show_extended_link && link_extended}
        isHeader={true}
      />
    </>
  );

  const breadcrumbsJSX = (
    <Breadcrumbs
      parents={parents}
      post={post}
      extraClass={breadcrumb_bg_color || ""}
      breadcrumbPositon={
        header.type === "splitheader" && splitHeaderDirection === "row"
          ? "is-justify-content-flex-end"
          : "is-justify-content-flex-start"
      }
    />
  );

  switch (type) {
    case "textheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          header={breadcrumbsJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(textheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment="center"
          height={"content-height"}
        />
      );
    case "imageheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          header={mediaJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(imageheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment={imageheaderTextHorizontalAlign || "start"}
          contentVerticalAlignment={imageheaderTextVerticalAlign || "start"}
          height={
            imageheaderImageHeight === "screen-height"
              ? "screen-height"
              : "content-height"
          }
          heightDependency="content"
          arrangement={
            imageheaderVerticalAlign === "overlay"
              ? "overlay"
              : imageheaderVerticalAlign === "media-top-text-bottom"
                ? "content-bottom"
                : "content-top"
          }
          gradient={imageheaderHeadlineGradient ?? false}
        />
      );
    case "splitheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          contentFrame={
            splitheaderImageHeight === "screen-height-frameless"
              ? "frameless"
              : "narrow-frame"
          }
          headerFrame={"frameless"}
          contentHorizontalAlignment="center"
          height={
            splitheaderImageHeight === "screen-height-frameless"
              ? "screen-height"
              : "content-height"
          }
          narrowFrameColumns={
            splitheaderImageHeight === "screen-height-frameless"
              ? "is-12"
              : columnSize(splitheaderWidth ?? false) || "is-12"
          }
          arrangement="content-bottom"
          header={
            splitheaderImageHeight === "screen-height-frameless"
              ? undefined
              : breadcrumbsJSX
          }
          content={
            <Split
              first={mediaJSX}
              firstClassName={"min-height-acceptor-desktop"}
              second={contentJSX}
              secondOuterFractional={
                splitheaderImageHeight === "screen-height-frameless"
                  ? breadcrumbsJSX
                  : undefined
              }
              direction={splitHeaderDirection}
              firstVerticalAlignment={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "stretch"
                  : "start"
              }
              secondVerticalAlignment={splitheaderTextVerticalAlign}
              secondClassName="section-padding-y"
              verticalBreapoints={"tablet"}
              sticky="first"
              stickyTop={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "var(--navbar-height)"
                  : "calc(var(--navbar-height) + var(--section-padding-y))"
              }
              stickyMaxHeight="calc(100dvh - var(--navbar-height))"
              gap={"8.3333%"}
              gapOn="second"
              firstWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                      };
                    }, {})
              }
              secondWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: bulmaColumnsToWidth(
                          curr.number_of_cols,
                        ),
                      };
                    }, {})
              }
              containerWidthLimit={{
                first: false,
                second: splitheaderImageHeight === "screen-height-frameless",
              }}
            />
          }
        />
      );
  }
};
